/**
 * Pulse Ingredients & Dosage
 * Duration calculator and interactive elements
 */

class PulseDurationCalculator extends HTMLElement {
  constructor() {
    super();
    
    this.servingsInput = this.querySelector('[data-servings-input]');
    this.containerInput = this.querySelector('[data-container-input]');
    this.durationDays = this.querySelector('[data-duration-days]');
    this.durationNote = this.querySelector('[data-duration-note]');
    
    this.defaultServingsPerDay = parseInt(this.dataset.servingsPerDay) || 1;
    this.defaultServingsPerContainer = parseInt(this.dataset.servingsPerContainer) || 30;
    
    this.init();
  }
  
  init() {
    // Set default values if inputs are empty
    if (this.servingsInput && !this.servingsInput.value) {
      this.servingsInput.value = this.defaultServingsPerDay;
    }
    if (this.containerInput && !this.containerInput.value) {
      this.containerInput.value = this.defaultServingsPerContainer;
    }
    
    // Calculate on input change
    if (this.servingsInput) {
      this.servingsInput.addEventListener('input', this.calculate.bind(this));
      this.servingsInput.addEventListener('change', this.calculate.bind(this));
    }
    
    if (this.containerInput) {
      this.containerInput.addEventListener('input', this.calculate.bind(this));
      this.containerInput.addEventListener('change', this.calculate.bind(this));
    }
    
    // Initial calculation
    this.calculate();
  }
  
  calculate() {
    const servingsPerDay = parseInt(this.servingsInput?.value) || this.defaultServingsPerDay;
    const servingsPerContainer = parseInt(this.containerInput?.value) || this.defaultServingsPerContainer;
    
    if (servingsPerDay <= 0 || servingsPerContainer <= 0) {
      this.updateResult(0, 'Please enter valid numbers');
      return;
    }
    
    const days = Math.floor(servingsPerContainer / servingsPerDay);
    const remainder = servingsPerContainer % servingsPerDay;
    
    let note = '';
    if (days === 0) {
      note = 'Less than one day';
    } else if (days === 1) {
      note = remainder > 0 
        ? `Approximately 1 day (${remainder} serving${remainder > 1 ? 's' : ''} remaining)`
        : 'Exactly 1 day';
    } else if (days < 7) {
      note = remainder > 0
        ? `About ${days} days (${remainder} serving${remainder > 1 ? 's' : ''} remaining)`
        : `Exactly ${days} days`;
    } else if (days < 30) {
      const weeks = Math.floor(days / 7);
      const remainingDays = days % 7;
      note = remainder > 0
        ? `About ${weeks} week${weeks > 1 ? 's' : ''}${remainingDays > 0 ? ` and ${remainingDays} day${remainingDays > 1 ? 's' : ''}` : ''}`
        : `Exactly ${weeks} week${weeks > 1 ? 's' : ''}${remainingDays > 0 ? ` and ${remainingDays} day${remainingDays > 1 ? 's' : ''}` : ''}`;
    } else {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      note = remainder > 0
        ? `About ${months} month${months > 1 ? 's' : ''}${remainingDays > 0 ? ` and ${remainingDays} day${remainingDays > 1 ? 's' : ''}` : ''}`
        : `Approximately ${months} month${months > 1 ? 's' : ''}${remainingDays > 0 ? ` and ${remainingDays} day${remainingDays > 1 ? 's' : ''}` : ''}`;
    }
    
    this.updateResult(days, note);
  }
  
  updateResult(days, note) {
    if (this.durationDays) {
      this.durationDays.textContent = days > 0 ? days : '—';
    }
    
    if (this.durationNote) {
      this.durationNote.textContent = note;
    }
    
    // Animate result
    if (this.durationDays) {
      this.durationDays.style.animation = 'none';
      setTimeout(() => {
        this.durationDays.style.animation = 'fade-in 0.3s ease';
      }, 10);
    }
  }
}

// Define custom element
if (!customElements.get('pulse-duration-calculator')) {
  customElements.define('pulse-duration-calculator', PulseDurationCalculator);
}

// Add fade-in animation
const style = document.createElement('style');
style.textContent = `
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;
document.head.appendChild(style);

// Export for global access
window.PulseDurationCalculator = PulseDurationCalculator;

